import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  AnalyticsService,
  InfoItem,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { DistributionHistory } from '@products/models/distribution-history';
import { DistributionChartService } from '@products/pricing-distribution/services/distribution-chart.service';
import { WindowScrollService } from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TableResponsiveService } from '@shared/table/table-responsive.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  DistributionLatestConfig,
  DistributionTableConfig,
  EDSPaginationPageChangedEvent,
  FundId,
  PaginationActionEnum,
  ProductType,
} from '@types';
import { generateUUID } from '@utils/text/string-utils';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
interface YearOption {
  label: string;
  value: string;
}

@Component({
  selector: 'ft-distribution-table-view',
  templateUrl: './distribution-table-view.component.html',
  styleUrls: ['./distribution-table-view.component.scss'],
  providers: [TableResponsiveService],
})
export class DistributionTableViewComponent
  extends AbstractBaseComponent
  implements OnInit, OnChanges, OnDestroy {
  public isHandheld$: Observable<boolean>;
  @Input() latestConfig: DistributionLatestConfig;
  @Input() tableConfig: DistributionTableConfig;
  @Input() filteredHistoricalList: DistributionHistory[];
  @Input() historicalTableTotalPages: {
    count: number;
  };
  @Input() fundId: FundId;
  @Input() isSiteIntl: boolean;
  @Input() configurationName: ConfigurationId;
  @Input() productType: ProductType;
  @Output() selectedYear = new EventEmitter<string>();
  @Output() selectedPage = new EventEmitter<number>();
  @Output() downloadAction = new EventEmitter();
  public historicalList: DistributionHistory[] = [];
  public totalPages: number;
  public showCanadaDistForEtfMfMmf: boolean;
  public tooltips: { [name: string]: string } = {};
  public hideExDistDate: boolean;

  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;
  cards: InfoItem[][] = [];
  pageTitle: string;
  @ViewChild('tableResponsive', { static: false })
  public set table(table: ElementRef) {
    if (table) {
      this.tableResponsiveService?.init(table);
    }
  }

  private unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private translateService: TranslateService,
    protected responsiveService: ResponsiveService,
    private analytics: AnalyticsService,
    private titleService: Title,
    protected tableResponsiveService: TableResponsiveService,
    private changeDetectorRef: ChangeDetectorRef,
    private distributionService: DistributionChartService,
    private scrollService: WindowScrollService
  ) {
    super();
    this.tableResponsiveService.cards$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cards = cards;
        this.changeDetectorRef.detectChanges();
      });
  }
  ngOnInit(): void {
    super.ngOnInit();
    this.showCanadaDistForEtfMfMmf = this.distributionService.showCanadaDistForEtfMfMmf(
      this.productType
    );
    this.pageTitle = this.titleService.getTitle();
    this.hideExDistDate = this.distributionService.hideExDistDateForCountry();
    this.historicalList = this.filteredHistoricalList;
    this.totalPages = this.historicalTableTotalPages?.count;
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.changeDetectorRef.detectChanges();
    this.setTooltips();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.filteredHistoricalList &&
      !changes.filteredHistoricalList.firstChange
    ) {
      this.historicalList = changes.filteredHistoricalList.currentValue;
      this.scrollService.scrollToAnchor('scroll');
    }

    if (
      changes.historicalTableTotalPages &&
      !changes.historicalTableTotalPages.firstChange
    ) {
      this.totalPages = changes.historicalTableTotalPages.currentValue.count;
      if (this.paginationComponent) {
        this.paginationComponent.activePage = 1;
        this.paginationComponent?.updatePagination(this.totalPages); // Perform changes on pagination component.
      }
    }
  }

  private setTooltips() {
    this.tooltips[
      'products.distributable-income-column-header'
    ] = this.translateService.tooltip(
      'products.distributable-income-column-header'
    );
    this.tooltips[
      'products.dividend-income-column-header'
    ] = this.translateService.tooltip('products.dividend-income-column-header');
  }

  /**
   * Return the boolean value for preference on income over capgain values.
   * @param distributionHistory distribution history object
   */
  public showIncomeOverCapgains(
    distributionHistory: DistributionHistory
  ): boolean {
    return !!distributionHistory.incomeDistributionExDividendDate;
  }

  /**
   * Trigger page change with the page number selected.
   * @param event page changed event generated
   */
  public onPageChanged(event: EDSPaginationPageChangedEvent): void {
    if (event.action !== 'load') {
      this.selectedPage.next(event.page);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
